import { Assembly, blob, latheZ } from '../geometry.js';

/**
 * Bare fists — the always-available fallback melee "weapon". No blade, no
 * ammunition: `WeaponSystem` branches on `def.class === 'melee'` and does a
 * short raycast instead of firing a projectile (see `src/weapons/index.js`).
 *
 * There is no gun to hold, so `body` only carries a pair of hand-wrap bands —
 * enough geometry for the wear/grime bake to have something to touch and for
 * the fists to read as equipped rather than as nothing. Both hands are
 * "gripped" independently (a boxer's guard), so gripR and gripL are two
 * separate targets in front of the chest rather than one weapon two hands
 * share.
 */
export function buildFists() {
  const body = new Assembly('fists-body');

  // Wrapped-knuckle bands, one under each hand's IK target, purely cosmetic.
  for (const sx of [1, -1]) {
    const wrapY = sx > 0 ? 0 : -0.01;
    const band = latheZ(
      [
        [0.017, 0],
        [0.019, 0.004],
        [0.019, 0.05],
        [0.017, 0.054],
      ],
      12
    );
    body.add(band, 'rubber', {
      x: sx * 0.09,
      y: 0.02 + wrapY,
      z: 0.04,
      rx: Math.PI / 2,
    });
    band.dispose();
    const knuckle = blob(0.034, 0.016, 0.02, 0.004, 2);
    body.add(knuckle, 'polymer', { x: sx * 0.09, y: 0.02 + wrapY, z: 0.0 });
    knuckle.dispose();
  }

  return {
    id: 'fists',
    label: 'Fists',
    fxClass: 'melee',
    body,
    moving: {},
    nodes: {
      muzzle: [0, 0.02, -0.02],
      eject: [0, 0.02, 0.04],
      sight: [0, 0.02, -0.2],
      sightAxis: [0, 0, -1],
      ironSight: [0, 0.02, -0.2],
      // Two independent fist targets rather than one weapon shared by both
      // hands — a raised, bladed boxer's guard, right fist slightly forward.
      gripR: {
        pos: [0.09, 0.02, 0.02],
        finger: [-0.1, -0.2, -0.96],
        back: [0.95, 0.25, -0.1],
      },
      gripL: {
        pos: [-0.09, 0.01, 0.06],
        finger: [0.15, -0.25, -0.94],
        back: [-0.94, 0.28, -0.12],
      },
      magSeat: { pos: [0, 0, 0], rot: [0, 0, 0] },
    },
    shell: { caseLen: 0.02, rimR: 0.005 },
    magSize: { len: 0.1, w: 0.02, d: 0.02 },
  };
}
